import { NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Actions, ofType } from '@ngrx/effects';
import { TranslatePipe } from '@ngx-translate/core';
import {
    AutoUnsubscribe,
    EstensaBusWrapperService,
    ToolbarAction,
    TooluploadState,
    UploadExternalData,
    seOpenTooluploadDialog,
} from '@se/platform';
import { Logger } from '@se/se.platform.base';
import EstensaModel from 'estensa-module';
import { isEmpty } from 'lodash-es';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { OriginPage } from '../../models';
import { PatientDetails } from '../dicom/models/dto/patient-details';
import { DicomLandingPage } from '../dicom/pages/landing-page/landing-page.component';
import { TooluploadFormComponent } from '../toolupload-form/toolupload-form.component';

@Component({
    selector: 'toolupload-dialog',
    templateUrl: './toolupload-dialog.component.html',
    styleUrls: ['./toolupload-dialog.component.scss'],
    imports: [NgIf, TooluploadFormComponent, DicomLandingPage, MatButton, TranslatePipe, MatProgressSpinnerModule],
    standalone: true,
})
export class TooluploadDialogComponent implements OnInit, OnDestroy {
    @ViewChild('tooluploadDialogTemplate')
    tooluploadDialogTemplate!: TemplateRef<any>;
    patientDetails: PatientDetails | null = null;
    toolbarAction: ToolbarAction | null = null;
    vnaUploadExternalData: UploadExternalData | null = null;
    updatedPatient: EstensaModel.Patient = null;
    isWaiting = false;
    private seVnaDialog!: MatDialogRef<any>;
    private actionsSubscription!: Subscription;

    private ngUnsubscribe: Subject<void> = new Subject<void>();

    // constructor

    constructor(
        public dialog: MatDialog,
        private actions: Actions,
        private estensaBusWrapperService: EstensaBusWrapperService,
        private logger: Logger,
    ) { }

    public ngOnInit() {
        this.listenForStoreEvents();
    }

    @AutoUnsubscribe()
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
        if (this.actionsSubscription) this.actionsSubscription.unsubscribe();
    }

    openNotDicomDialog() {
        this.seVnaDialog = this.dialog.open(this.tooluploadDialogTemplate, {
            autoFocus: true,
            height: '98%',
            minWidth: '70vw',
            minHeight: '500px',
            maxWidth: '70vw',
            disableClose: true,
        });

        this.seVnaDialog.afterOpened().subscribe(() => {
            this.isWaiting = false;
        })
    }

    closeModal() {
        this.seVnaDialog.close(true);
        if (this.vnaUploadExternalData?.from === OriginPage.VNA_PATIENT_FOLDER && !this.vnaUploadExternalData.type) {
            this.estensaBusWrapperService.notify({
                messageType: 'CLOSE_MODAL_TOOLUPLOAD',
                data: { patient: this.vnaUploadExternalData.selectedPatient },
            });
        }
        if (this.vnaUploadExternalData?.from === OriginPage.VNA_PATIENT_FOLDER && this.vnaUploadExternalData.type) {
            this.estensaBusWrapperService.notify({
                messageType: 'CLOSE_MODAL_TOOLUPLOAD',
                data: { patientId: this.vnaUploadExternalData?.selection?.identifier },
            });
        }
    }

    // #region Dialog

    getPatientDetails(data: UploadExternalData): PatientDetails {
        if (data) {
            const patientDetails: PatientDetails = this.setData();
            return patientDetails;
        } else {
            this.logger.error('patient details data error');
            return {
                Issuer: '',
                PatientId: '',
                Name: '',
                Surname: '',
                Sex: '',
                BirthDate: '',
                AccessionNumber: '',
            };
        }
    }

    private listenForStoreEvents() {
        this.actionsSubscription = this.actions
            .pipe(takeUntil(this.ngUnsubscribe), ofType(seOpenTooluploadDialog))
            .subscribe({
                next: (data: TooluploadState) => {
                    this.isWaiting = true;
                    let dataState = data.data;
                    if (this.typeDicom(dataState)) {
                        this.vnaUploadExternalData = dataState;
                        this.patientDetails = this.getPatientDetails(this.vnaUploadExternalData);
                        this.openDicomDialog();
                    } else {
                        if (
                            typeof dataState.selectedPatient === 'string' ||
                            typeof dataState.selectedExamination === 'string'
                        ) {
                            dataState.selectedPatient
                                ? this.deserializedDataPatient(dataState)
                                : this.deserializedDataExamination(dataState);
                        } else {
                            this.vnaUploadExternalData = dataState;
                        }
                        this.openNotDicomDialog();
                    }
                    this.toolbarAction = dataState.toolbarAction;
                },
                error: () => {
                    this.logger.error('no data retrieved');
                },
                complete: () => {
                    this.isWaiting = false;
                }
            });
    }

    private typeDicom(data: UploadExternalData): boolean {
        return !isEmpty(data.type);
    }

    // #endregion

    // #region Get/Set Data

    private openDicomDialog() {
        this.seVnaDialog = this.dialog.open(this.tooluploadDialogTemplate, {
            autoFocus: true,
            maxHeight: '640px',
            disableClose: true,
        });
    }

    private setData(): PatientDetails {
        return {
            Issuer: this.vnaUploadExternalData?.selection?.issuer,
            PatientId: this.vnaUploadExternalData?.selection?.identifier,
            Name: this.vnaUploadExternalData?.selection?.firstName,
            Surname: this.vnaUploadExternalData?.selection?.lastName,
            Sex: this.vnaUploadExternalData?.selection?.sexCode,
            BirthDate: this.vnaUploadExternalData?.selection?.birthDate,
            AccessionNumber: this.vnaUploadExternalData?.selection?.accessionNumber,
        };
    }

    private deserializedDataPatient(dataState: UploadExternalData) {
        let patient = dataState.selectedPatient as any;
        patient = JSON.parse(dataState.selectedPatient as any);
        this.vnaUploadExternalData = {
            from: dataState.from,
            selectedPatient: patient,
            loggedUser: dataState.loggedUser,
            toolbarAction: dataState.toolbarAction ? dataState.toolbarAction : null
        };
    }

    private deserializedDataExamination(dataState: UploadExternalData) {
        let exam = dataState.selectedExamination as any;
        exam = JSON.parse(dataState.selectedExamination as any);
        this.vnaUploadExternalData = {
            from: dataState.from,
            selectedExamination: exam,
            loggedUser: dataState.loggedUser,
            toolbarAction: dataState.toolbarAction ? dataState.toolbarAction : null
        };
    }

    // #endregion
}
